import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * Normalized option shape consumed by `UserPicker`. Keeping the picker
 * decoupled from any specific DTO lets the same component back the
 * future "ordering physician" / "referring physician" fields without
 * changes — the caller maps its own DTO to this shape.
 */
export interface UserPickerOption {
  /** Stable identifier (used for keys + selection equality). */
  readonly guid: string;
  /** Primary line — typically `${first_name} ${last_name}`. */
  readonly fullName: string;
  /** Secondary line — e.g. role title or department. Optional. */
  readonly subtitle?: string | null;
}

interface UserPickerProps {
  readonly value: UserPickerOption | null;
  readonly onChange: (next: UserPickerOption | null) => void;
  readonly options: readonly UserPickerOption[];
  readonly isLoading?: boolean;
  readonly placeholder?: string;
  readonly id?: string;
  readonly disabled?: boolean;
  readonly ariaLabel?: string;
  /**
   * Optional controlled query. When both `query` and `onQueryChange` are
   * supplied the picker delegates the search input to the parent and skips
   * its built-in client-side filter — `options` is rendered as-is. Callers
   * use this to wire a server-side typeahead. Omit for the legacy
   * pre-fetch + client-filter mode.
   */
  readonly query?: string;
  readonly onQueryChange?: (next: string) => void;
}

/**
 * Searchable user picker. Empty state shows a flat input; selected state
 * shows a pill with an ✕ to clear. Keyboard nav: ↑↓ moves selection,
 * Enter commits, Esc closes the dropdown without changing the value.
 *
 * Filters client-side on full name + subtitle. Suitable for tenant-sized
 * lists (<a few hundred users); swap to a server-side typeahead if the
 * options list ever needs paging.
 */
export function UserPicker({
  value,
  onChange,
  options,
  isLoading,
  placeholder,
  id,
  disabled,
  ariaLabel,
  query: controlledQuery,
  onQueryChange,
}: UserPickerProps): ReactElement {
  const { t } = useTranslation();
  const reactId = useId();
  const fieldId = id ?? reactId;
  const listboxId = `${fieldId}-listbox`;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [internalQuery, setInternalQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);

  const isControlled = controlledQuery !== undefined && onQueryChange !== undefined;
  const query = isControlled ? controlledQuery : internalQuery;
  const setQuery = isControlled ? onQueryChange : setInternalQuery;

  const trimmed = query.trim().toLowerCase();
  // In controlled (server-typeahead) mode the parent has already filtered
  // `options` by querying the backend — render as-is. In uncontrolled mode
  // fall back to the local substring filter.
  const filtered = isControlled
    ? options
    : trimmed.length === 0
      ? options
      : options.filter((option) => {
          const haystack = `${option.fullName} ${option.subtitle ?? ''}`.toLowerCase();
          return haystack.includes(trimmed);
        });

  useEffect(() => {
    setHighlighted(0);
  }, [trimmed, options]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent): void => {
      const root = containerRef.current;
      if (root === null) return;
      if (!root.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const commit = (option: UserPickerOption): void => {
    onChange(option);
    setQuery('');
    setOpen(false);
  };

  const clear = (): void => {
    onChange(null);
    setQuery('');
    setOpen(true);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) setOpen(true);
      setHighlighted((prev) =>
        Math.min(prev + 1, Math.max(filtered.length - 1, 0)),
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((prev) => Math.max(prev - 1, 0));
    } else if (event.key === 'Enter') {
      const target = filtered[highlighted];
      if (target !== undefined) {
        event.preventDefault();
        commit(target);
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
    }
  };

  if (value !== null) {
    return (
      <div ref={containerRef} className="flex items-center gap-2">
        <span
          className={cn(
            'inline-flex items-center gap-2 rounded-md border border-border bg-card pl-2 pr-1 py-1 text-sm flex-1 min-w-0',
            disabled === true && 'opacity-60',
          )}
        >
          <span
            aria-hidden="true"
            className="h-6 w-6 shrink-0 rounded-full bg-brand text-brand-foreground inline-flex items-center justify-center text-[10px] font-semibold"
          >
            {initialsFor(value.fullName)}
          </span>
          <span className="min-w-0 flex flex-col leading-tight flex-1">
            <span className="text-[13px] font-medium text-foreground truncate">
              {value.fullName}
            </span>
            {value.subtitle !== null && value.subtitle !== undefined && (
              <span className="text-[11px] text-muted-foreground truncate">
                {value.subtitle}
              </span>
            )}
          </span>
          <button
            type="button"
            onClick={clear}
            disabled={disabled === true}
            aria-label={t('userPicker.clear')}
            className="h-6 w-6 shrink-0 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </span>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center gap-2 rounded-md border border-border bg-card px-2.5 h-9 focus-within:ring-2 focus-within:ring-ring">
        <Search
          className="h-3.5 w-3.5 text-muted-foreground shrink-0"
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          id={fieldId}
          type="text"
          value={query}
          disabled={disabled === true}
          placeholder={placeholder ?? t('userPicker.placeholder')}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-label={ariaLabel}
          aria-activedescendant={
            open && filtered.length > 0
              ? `${listboxId}-option-${String(highlighted)}`
              : undefined
          }
          className="flex-1 bg-transparent border-0 outline-none text-sm text-foreground placeholder:text-muted-foreground"
        />
      </div>
      {open && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-50 mt-1 left-0 right-0 max-h-56 overflow-y-auto rounded-md border border-border bg-popover shadow-lg"
        >
          {isLoading === true && (
            <p className="px-3 py-2 text-xs text-muted-foreground">
              {t('userPicker.loading')}
            </p>
          )}
          {isLoading !== true && filtered.length === 0 && (
            <p className="px-3 py-2 text-xs text-muted-foreground">
              {trimmed.length > 0
                ? t('userPicker.noMatches', { query: query.trim() })
                : t('userPicker.empty')}
            </p>
          )}
          {filtered.map((option, idx) => (
            <button
              type="button"
              key={option.guid}
              id={`${listboxId}-option-${String(idx)}`}
              role="option"
              aria-selected={idx === highlighted}
              onMouseEnter={() => {
                setHighlighted(idx);
              }}
              onClick={() => {
                commit(option);
              }}
              className={cn(
                'w-full text-left px-3 py-2 flex items-center gap-2.5 transition-colors',
                idx === highlighted ? 'bg-accent' : 'hover:bg-accent/60',
              )}
            >
              <span
                aria-hidden="true"
                className="h-6 w-6 shrink-0 rounded-full bg-brand text-brand-foreground inline-flex items-center justify-center text-[10px] font-semibold"
              >
                {initialsFor(option.fullName)}
              </span>
              <span className="min-w-0 flex flex-col leading-tight">
                <span className="text-[13px] font-medium text-foreground truncate">
                  {option.fullName}
                </span>
                {option.subtitle !== null && option.subtitle !== undefined && (
                  <span className="text-[11px] text-muted-foreground truncate">
                    {option.subtitle}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0]?.charAt(0) ?? '?').toUpperCase();
  const first = parts[0]?.charAt(0) ?? '';
  const last = parts[parts.length - 1]?.charAt(0) ?? '';
  return `${first}${last}`.toUpperCase();
}
